import { useEffect, useRef } from "react";

import { MedplumClient } from "@medplum/core";
import { useMedplum } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { isPublicAuthPathname } from "@/utils/medplum";

/**
 * Hook that manages periodic token refresh to prevent session expiration during active use.
 *
 * Refreshes the access token every 5 minutes while the page is visible, preventing
 * session expiration during long forms or note-taking sessions (e.g., JIRA PE-3442).
 *
 * Features:
 * - Refreshes token every 5 minutes (300000ms)
 * - Only refreshes when page is visible (Page Visibility API)
 * - Silent operation - no user notifications
 * - Automatic cleanup on unmount
 * - Error handling with optional Sentry tracking
 *
 * @param medplum - Optional MedplumClient instance. If not provided, uses useMedplum() hook.
 *
 * @example
 * ```tsx
 * function AppShell() {
 *   const medplum = useMedplum();
 *   useSessionManager(medplum);
 *   // ... rest of component
 * }
 * ```
 */
export function useSessionManager(medplum?: MedplumClient): void {
  const medplumFromHook = useMedplum();
  const medplumClient = medplum ?? medplumFromHook;
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    const REFRESH_INTERVAL = 300000; // 5 minutes in milliseconds
    let refreshInFlight = false;

    const refreshToken = async () => {
      // Check visibility on every tick.
      if (document.visibilityState !== "visible") {
        return;
      }

      // Sign-in and OAuth surfaces: periodic auth/me was rehydrating Medplum localStorage
      // (activeLogin/logins) from refresh cookies without the user submitting credentials.
      // Read window.location at tick time so this is always current without needing a router hook.
      if (isPublicAuthPathname(window.location.pathname)) {
        return;
      }

      // No session to extend — avoid triggering a token fetch on an unauthenticated tab.
      if (!medplumClient.getActiveLogin()) {
        return;
      }

      // Prevent overlapping refresh calls if one takes longer than the interval
      if (refreshInFlight) {
        return;
      }

      refreshInFlight = true;
      try {
        // Trigger token refresh by making a lightweight API call
        // The Medplum SDK automatically refreshes tokens before API calls when within grace period
        // We use get("auth/me") to force a network request and trigger refreshIfExpired().
        await medplumClient.get("auth/me");
      } catch (error) {
        // Silent failure - let existing onUnauthenticated handler show notification
        // Error tracking for monitoring (not user notification)
        Sentry.captureException(error, {
          tags: { component: "SessionManager" },
        });
      } finally {
        refreshInFlight = false;
      }
    };

    // Set up interval for periodic refresh
    intervalRef.current = setInterval(refreshToken, REFRESH_INTERVAL);

    // Cleanup function to clear interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, [medplumClient]);
}
